/**
 * VectraClip — Theme Storage (VEC-195)
 *
 * Gerencia persistência e aplicação do tema global em `<html data-theme="...">`.
 * Convenção: tokens CSS definidos em `src/index.css` por `[data-theme='...']`
 * para os temas principais; src/styles/themes.css traz as paletas autorais
 * V1..V6 (galeria histórica `/themes`).
 *
 * Fonte da verdade:
 *   - `localStorage[THEME_STORAGE_KEY]` → escolha explícita do usuário.
 *   - `DEFAULT_THEME` → usado se nada estiver persistido.
 *
 * Fora de escopo (backlog): sincronização com `prefers-color-scheme`,
 * temas por tenant.
 */

export const THEME_STORAGE_KEY = 'vectraclip-theme' as const

export const THEMES = [
  // Default + paletas autorais Vectra (galeria /themes — V3 = vectra-v3)
  'vectra-v3',
  'theme-v1',
  'theme-v2',
  'theme-v4',
  'theme-v5',
  'theme-v6',
  // Outras paletas selecionáveis pelo UserMenu
  'solarized-light',
  'tokyo-night-light',
  'vscode-dark-plus',
  'vscode-dark-modern',
  'vscode-light-plus',
  'vscode-light-modern',
  'vscode-dark-vs',
  'vscode-light-vs',
  'vscode-dark-hc',
  'vscode-light-hc',
] as const
export type Theme = (typeof THEMES)[number]

export const DEFAULT_THEME: Theme = 'vectra-v3'

export interface ThemeMeta {
  id: Theme
  /** Label exibido na UI (PT-BR). */
  label: string
  /** Curta descrição para tooltips / menu. */
  description: string
  /** Aparência subjacente — usado para sincronizar Sonner, ícones etc. */
  scheme: 'dark' | 'light'
}

export const THEME_META: Record<Theme, ThemeMeta> = {
  'vectra-v3': {
    id: 'vectra-v3',
    label: 'V3 — Metal Escovado (default)',
    description: 'Cinza-aço médio com cobre Vectra. Padrão do plano de controle.',
    scheme: 'dark',
  },
  'theme-v1': {
    id: 'theme-v1',
    label: 'V1 — Dark Navy + Laranja',
    description: 'Convenção antiga. Background azul-marinho profundo, brand laranja Vectra.',
    scheme: 'dark',
  },
  'theme-v2': {
    id: 'theme-v2',
    label: 'V2 — Graphite Escuro',
    description: 'Graphite quase preto + cobre. Vibe control room industrial.',
    scheme: 'dark',
  },
  'theme-v4': {
    id: 'theme-v4',
    label: 'V4 — Carcaça + Vermelho Neural',
    description: 'Prateado luminoso + vermelho neural dos circuitos. Light high-tech.',
    scheme: 'light',
  },
  'theme-v5': {
    id: 'theme-v5',
    label: 'V5 — Cobre Patinado + Esmeralda',
    description: 'Metal envelhecido com pátina + esmeralda neural fria.',
    scheme: 'light',
  },
  'theme-v6': {
    id: 'theme-v6',
    label: 'V6 — Matéria Escura + Próton Roxo',
    description: 'Fibra de carbono + brand próton roxo. Cyberpunk limpo.',
    scheme: 'dark',
  },
  'solarized-light': {
    id: 'solarized-light',
    label: 'Solarized (claro)',
    description: 'Paleta clara Solarized com brand Vectra preservado.',
    scheme: 'light',
  },
  'tokyo-night-light': {
    id: 'tokyo-night-light',
    label: 'Tokyo Night Light',
    description: 'Paleta clara Tokyo Night com roxo neural como brand.',
    scheme: 'light',
  },
  'vscode-dark-plus': {
    id: 'vscode-dark-plus',
    label: 'Dark+',
    description: 'Tema escuro padrão do VS Code com azul cornflower como brand.',
    scheme: 'dark',
  },
  'vscode-dark-modern': {
    id: 'vscode-dark-modern',
    label: 'Dark Modern',
    description: 'Tema escuro moderno do VS Code 1.70+ com Microsoft blue.',
    scheme: 'dark',
  },
  'vscode-light-plus': {
    id: 'vscode-light-plus',
    label: 'Light+',
    description: 'Tema claro padrão do VS Code com fundo branco puro.',
    scheme: 'light',
  },
  'vscode-light-modern': {
    id: 'vscode-light-modern',
    label: 'Light Modern',
    description: 'Tema claro moderno do VS Code 1.70+ com Microsoft blue.',
    scheme: 'light',
  },
  'vscode-dark-vs': {
    id: 'vscode-dark-vs',
    label: 'Dark (Visual Studio)',
    description: 'Tema escuro clássico do Visual Studio com azul claro como brand.',
    scheme: 'dark',
  },
  'vscode-light-vs': {
    id: 'vscode-light-vs',
    label: 'Light (Visual Studio)',
    description: 'Tema claro clássico do Visual Studio com fundo #f5f5f5.',
    scheme: 'light',
  },
  'vscode-dark-hc': {
    id: 'vscode-dark-hc',
    label: 'Dark High Contrast',
    description: 'Preto puro com cyan — máximo contraste para acessibilidade.',
    scheme: 'dark',
  },
  'vscode-light-hc': {
    id: 'vscode-light-hc',
    label: 'Light High Contrast',
    description: 'Branco puro com bordas pretas — máximo contraste para acessibilidade.',
    scheme: 'light',
  },
}

/** Cor canônica do background — vai pro `<meta name="theme-color">`. */
const THEME_COLOR_MAP: Record<Theme, string> = {
  'vectra-v3': '#2A2E36',
  'theme-v1': '#0B1426',
  'theme-v2': '#0F1114',
  'theme-v4': '#A8AEB5',
  'theme-v5': '#92AD9A',
  'theme-v6': '#0F1113',
  'solarized-light': '#fdf6e3',
  'tokyo-night-light': '#d5d6db',
  'vscode-dark-plus': '#1e1e1e',
  'vscode-dark-modern': '#1f1f1f',
  'vscode-light-plus': '#ffffff',
  'vscode-light-modern': '#ffffff',
  'vscode-dark-vs': '#1e1e1e',
  'vscode-light-vs': '#f5f5f5',
  'vscode-dark-hc': '#000000',
  'vscode-light-hc': '#ffffff',
}

function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value)
}

/**
 * Migra IDs antigos para o esquema atual. Mantém o storage limpo conforme
 * usuários voltam ao app.
 */
function migrateLegacyId(value: string): string {
  // 'theme-v3' foi um ID experimental durante a unificação dos temas. A paleta
  // é idêntica ao 'vectra-v3' (default histórico), então redirecionamos.
  if (value === 'theme-v3') return 'vectra-v3'
  return value
}

/**
 * Lê a preferência persistida. Retorna `DEFAULT_THEME` se não houver valor
 * válido ou se estivermos em SSR (sem `window`). Migra IDs antigos in-place.
 */
export function getStoredTheme(): Theme {
  if (typeof window === 'undefined') return DEFAULT_THEME
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY)
    if (raw === null) return DEFAULT_THEME
    const migrated = migrateLegacyId(raw)
    if (migrated !== raw) {
      // Best-effort: persiste o ID novo pra não migrar de novo no próximo boot.
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, migrated)
      } catch {
        // Storage bloqueado — segue com o valor migrado em memória.
      }
    }
    return isTheme(migrated) ? migrated : DEFAULT_THEME
  } catch {
    // localStorage pode estar bloqueado (Safari privado, iframe etc).
    return DEFAULT_THEME
  }
}

/** Persiste a escolha do usuário (silencioso em caso de bloqueio). */
export function setStoredTheme(theme: Theme): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme)
  } catch {
    // Ignora: a aplicação no DOM continua funcionando dentro da sessão.
  }
}

/**
 * Aplica o tema ao `<html>`. Faz o trabalho mínimo para que o CSS reaja:
 *   - `data-theme` → ativa o bloco `[data-theme='...']`.
 *   - `class` dark/light → compatibilidade com utilitários `dark:` caso
 *     sejam adicionados no futuro (Tailwind lê `darkMode: 'class'`).
 *   - `meta[name="theme-color"]` → barra de endereço em mobile.
 */
export function applyThemeToDocument(theme: Theme): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  const meta = THEME_META[theme]

  root.setAttribute('data-theme', theme)
  root.classList.remove('dark', 'light')
  root.classList.add(meta.scheme)

  const metaTag = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  if (metaTag) metaTag.content = THEME_COLOR_MAP[theme]
}

/**
 * Conveniência: lê do storage e aplica. Use no bootstrap (após anti-FOUC
 * inline no `index.html`, como reforço reativo).
 */
export function hydrateThemeFromStorage(): Theme {
  const theme = getStoredTheme()
  applyThemeToDocument(theme)
  return theme
}
